import type { RxCollection } from 'rxdb';
import { v4 as uuid } from 'uuid';
import type { ProjectDocType } from '@ast-data/shared';

/**
 * A "family" is the set of projects that share Befunde/Flurstücke — the linked
 * Teilmaßnahmen. Membership is a partition keyed by `familyId` (a shared uuid,
 * decoupled from the number so a new Amt base can still join). Empty familyId =
 * standalone.
 */

/**
 * All project ids in `familyId`'s family (always incl. `selfId`). A standalone
 * project (empty familyId) resolves to just itself.
 */
export function familyProjectIds(
    projects: readonly { id: string; familyId: string }[],
    familyId: string,
    selfId: string
): string[] {
    if (!familyId) {
        return [selfId];
    }
    const ids = projects
        .filter((project) => project.familyId === familyId)
        .map((project) => project.id);
    if (!ids.includes(selfId)) {
        ids.push(selfId);
    }
    return ids;
}

/**
 * Reconcile family membership after editing `projectId`'s linked-projects set.
 * `linkedIds` are the OTHER projects it should share with. Assigns one shared
 * `familyId` across {this} ∪ linkedIds (keeping the current one, else adopting
 * a member's, else a new uuid), and clears members this project removed.
 * Selecting nothing makes this project standalone; if that would leave a lone
 * straggler, the whole family is dissolved (no families of one).
 *
 * Admin/PM-only path; the server enforces CASL on the resulting pushes.
 */
export async function syncFamily(
    collection: RxCollection<ProjectDocType>,
    projectId: string,
    oldFamilyId: string,
    linkedIds: readonly string[]
): Promise<void> {
    const desired = new Set<string>([
        projectId,
        ...linkedIds.filter((id) => id !== projectId)
    ]);
    const all = await collection.find().exec();
    const stamp = () => new Date().toISOString();

    // Going standalone: clear this project. If removing it would leave a single
    // stranded member (a "family of one"), clear that member too — otherwise the
    // rest stay a family among themselves.
    if (desired.size <= 1) {
        if (oldFamilyId) {
            const oldMembers = all.filter(
                (doc) => doc.familyId === oldFamilyId
            );
            const clearAll = oldMembers.length - 1 <= 1;
            for (const doc of oldMembers) {
                if ((clearAll || doc.id === projectId) && doc.familyId) {
                    await doc.incrementalPatch({
                        familyId: '',
                        updatedAt: stamp()
                    });
                }
            }
        }
        return;
    }

    // Pick the shared id: keep the current family, else adopt a member's, else new.
    let familyId = oldFamilyId;
    if (!familyId) {
        const anchor = all.find(
            (doc) => desired.has(doc.id) && doc.familyId !== ''
        );
        familyId = anchor ? anchor.familyId : uuid();
    }

    for (const doc of all) {
        const inDesired = desired.has(doc.id);
        const inOldFamily = oldFamilyId !== '' && doc.familyId === oldFamilyId;
        if (inDesired && doc.familyId !== familyId) {
            await doc.incrementalPatch({ familyId, updatedAt: stamp() });
        } else if (!inDesired && inOldFamily) {
            await doc.incrementalPatch({ familyId: '', updatedAt: stamp() });
        }
    }
}
